import { useState, useCallback } from 'react';
import type { ReviewType } from '@wineryxplorer/shared';
import { trpc } from '../../lib/trpc';
import { useAuth } from '../../hooks/useAuth';
import { useEmailVerificationGate } from '../../hooks/useEmailVerificationGate';
import { ReviewCard, type AnySerializedReview } from './ReviewCard';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';

export type SortOption = 'createdAt' | 'ratings.overall' | 'helpfulCount';
export type SortOrder = 'asc' | 'desc';

export interface ReviewListProps {
  entityId: string;
  reviewType: ReviewType;
  pageSize?: number;
  showSorting?: boolean;
  isVenueManager?: boolean;
  className?: string;
}

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'createdAt', label: 'Most Recent' },
  { value: 'ratings.overall', label: 'Highest Rated' },
  { value: 'helpfulCount', label: 'Most Helpful' },
];

// Track optimistic vote changes per review
type VoteType = 'helpful' | 'notHelpful';
interface OptimisticVote {
  delta: { helpful: number; notHelpful: number };
  userVote: VoteType;
}

export const ReviewList = ({
  entityId,
  reviewType,
  pageSize = 10,
  showSorting = true,
  isVenueManager = false,
  className = '',
}: ReviewListProps) => {
  const { user } = useAuth();
  const { requireVerified, gateModal } = useEmailVerificationGate();
  const [sortBy, setSortBy] = useState<SortOption>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [offset, setOffset] = useState(0);
  const [optimisticVotes, setOptimisticVotes] = useState<Partial<Record<string, OptimisticVote>>>({});

  // Build the query input based on review type
  const queryInput = {
    limit: pageSize,
    offset,
    sortBy,
    sortOrder,
  };

  // Use the appropriate query based on review type
  const venueQuery = trpc.venueReview.list.useQuery(
    { ...queryInput, venueId: entityId },
    { enabled: reviewType === 'venue' }
  );

  const eventQuery = trpc.eventReview.list.useQuery(
    { ...queryInput, eventId: entityId },
    { enabled: reviewType === 'event' }
  );

  const experienceQuery = trpc.experienceReview.list.useQuery(
    { ...queryInput, experienceId: entityId },
    { enabled: reviewType === 'experience' }
  );

  const wineQuery = trpc.wineReview.list.useQuery(
    { ...queryInput, wineId: entityId },
    { enabled: reviewType === 'wine' }
  );

  // Get the active query based on review type
  const getActiveQuery = () => {
    switch (reviewType) {
      case 'venue':
        return venueQuery;
      case 'event':
        return eventQuery;
      case 'experience':
        return experienceQuery;
      case 'wine':
        return wineQuery;
    }
  };

  const activeQuery = getActiveQuery();
  const { data, isLoading, error, refetch } = activeQuery;

  // Vote mutation - fires in background, UI already updated optimistically
  const voteMutation = trpc.reviewActions.vote.useMutation({
    onError: (error, variables) => {
      // Revert optimistic update on error
      setOptimisticVotes((prev) => {
        const updated = { ...prev };
        delete updated[variables.reviewId];
        return updated;
      });
      console.error('Vote failed:', error.message);
    },
  });

  // Flag mutation
  const flagMutation = trpc.reviewActions.communityFlag.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  // Venue manager flag mutation
  const venueManagerFlagMutation = trpc.venueManagerReview.flag.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  const handleVote = useCallback(
    (reviewId: string, voteType: VoteType) => {
      if (!user) return;
      if (!requireVerified()) return;

      // Check if already voted this way
      const existingVote = optimisticVotes[reviewId];
      if (existingVote?.userVote === voteType) return;

      // Apply optimistic update immediately
      setOptimisticVotes((prev) => ({
        ...prev,
        [reviewId]: {
          delta: {
            helpful: voteType === 'helpful' ? 1 : 0,
            notHelpful: voteType === 'notHelpful' ? 1 : 0,
          },
          userVote: voteType,
        },
      }));

      // Fire mutation in background
      voteMutation.mutate({ reviewType, reviewId, voteType });
    },
    [user, requireVerified, reviewType, voteMutation, optimisticVotes]
  );

  const handleFlag = useCallback(
    (reviewId: string, flagType: 'OFFENSIVE' | 'INAPPROPRIATE' | 'FAKE') => {
      if (!user) return;
      if (!requireVerified()) return;
      flagMutation.mutate({ reviewType, reviewId, flagType });
    },
    [user, requireVerified, reviewType, flagMutation]
  );

  const handleVenueManagerFlag = useCallback(
    (reviewId: string) => {
      if (!user) return;
      if (!requireVerified()) return;
      venueManagerFlagMutation.mutate({ reviewType, reviewId });
    },
    [user, requireVerified, reviewType, venueManagerFlagMutation]
  );

  // Get optimistic vote data for a review, returns undefined if no optimistic update exists
  const getOptimisticVote = (reviewId: string): OptimisticVote | undefined => {
    return optimisticVotes[reviewId];
  };

  const handleSortChange = (newSortBy: SortOption) => {
    if (newSortBy === sortBy) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    setOffset(0); // Reset to first page
  };

  const handleNextPage = () => {
    if (data && offset + pageSize < data.total) {
      setOffset(offset + pageSize);
    }
  };

  const handlePrevPage = () => {
    if (offset > 0) {
      setOffset(Math.max(0, offset - pageSize));
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={`flex justify-center py-8 ${className}`}>
        <LoadingSpinner />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <p className="text-red-600 mb-4">Failed to load reviews</p>
        <Button variant="outline" onClick={() => refetch()}>
          Try Again
        </Button>
      </div>
    );
  }

  const reviews = (data?.items || []) as AnySerializedReview[];
  const total = data?.total || 0;
  const currentPage = Math.floor(offset / pageSize) + 1;
  const totalPages = Math.ceil(total / pageSize);

  // Empty state
  if (reviews.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <svg
          className="w-12 h-12 mx-auto text-gray-400 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        <p className="text-gray-500">No reviews yet</p>
        <p className="text-sm text-gray-400 mt-1">Be the first to share your experience!</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {gateModal}
      {/* Header with count and sorting */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {total} Review{total !== 1 ? 's' : ''}
        </h3>

        {showSorting && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Sort by:</span>
            <div className="flex gap-1">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSortChange(option.value)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                    sortBy === option.value
                      ? 'bg-primary-100 text-primary-700 font-medium'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                  {sortBy === option.value && (
                    <span className="ml-1">
                      {sortOrder === 'desc' ? '↓' : '↑'}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Review cards */}
      <div className="space-y-4">
        {reviews.map((review) => {
          const optimistic = getOptimisticVote(review._id);
          // Apply optimistic vote counts if user has voted
          const displayReview = optimistic
            ? {
                ...review,
                helpfulCount: review.helpfulCount + optimistic.delta.helpful,
                notHelpfulCount: review.notHelpfulCount + optimistic.delta.notHelpful,
              }
            : review;

          const userVoteStatus = optimistic
            ? { hasVoted: true as const, voteType: optimistic.userVote }
            : undefined;

          return (
            <ReviewCard
              key={review._id}
              review={displayReview}
              reviewType={reviewType}
              onVote={handleVote}
              onFlag={handleFlag}
              onVenueManagerFlag={isVenueManager ? handleVenueManagerFlag : undefined}
              isVenueManager={isVenueManager}
              userVoteStatus={userVoteStatus}
              isVoting={false}
              showActions={!!user}
              isOwnReview={user?.id === review.userId}
            />
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handlePrevPage}
            disabled={offset === 0}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={handleNextPage}
            disabled={offset + pageSize >= total}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};
